// design-system-exempt: a <canvas> cannot read a CSS variable, so this resolves the
// tokens through getComputedStyle. The literals are the FALLBACKS for the moment
// before the stylesheet has applied; they are copies of the light theme's values.
// The network, drawn on canvas with a force layout.
//
// A force layout rather than a ranked left-to-right flow, because the question this
// view answers is "what is connected to what", and a spring layout puts the answer in
// the geometry: clusters are the relationships, bridges are the pieces of work that
// travel, and a node's size is how load-bearing it is.
//
// Canvas rather than SVG: several hundred nodes and a thousand springs at 60fps means
// no per-node DOM. The physics is hand-rolled (repulsion, springs, weak centring)
// because that's about forty lines and skips a dependency.

import { useEffect, useRef } from 'react'

import { accentVar, KINDS, type EntityKind } from '../lib/model'
import { EDGE_HUE, type EdgeKind, type NetEdge, type Network, type NetNode } from '../lib/network'

interface Body {
  x: number
  y: number
  vx: number
  vy: number
  r: number
}

/** Springs are shorter for structural edges than for prose ones, so the schema's
 *  relationships cluster tightly and a passing @mention only tugs. */
const REST: Record<EdgeKind, number> = {
  party: 120,
  work: 132,
  builds: 130,
  renews: 90,
  owns: 130,
  mention: 210,
}

const STIFF: Record<EdgeKind, number> = {
  party: 0.02,
  work: 0.017,
  builds: 0.018,
  renews: 0.03,
  owns: 0.012,
  mention: 0.005,
}

/**
 * A node's outline, by kind: work is a circle, a deal is a diamond, an org is a
 * rounded-inline square, a person is a circle (drawn with an inner dot). Shape carries the
 * kind on its own, so the colours don't have to do it alone.
 */
function nodePath(
  ctx: CanvasRenderingContext2D,
  kind: EntityKind,
  x: number,
  y: number,
  r: number,
) {
  ctx.beginPath()
  if (kind === 'deal') {
    const d = r * 1.18
    ctx.moveTo(x, y - d)
    ctx.lineTo(x + d, y)
    ctx.lineTo(x, y + d)
    ctx.lineTo(x - d, y)
    ctx.closePath()
    return
  }
  if (kind === 'org') {
    const s = r * 0.94
    const c = Math.max(2, s * 0.28)
    ctx.moveTo(x - s + c, y - s)
    ctx.arcTo(x + s, y - s, x + s, y + s, c)
    ctx.arcTo(x + s, y + s, x - s, y + s, c)
    ctx.arcTo(x - s, y + s, x - s, y - s, c)
    ctx.arcTo(x - s, y - s, x + s, y - s, c)
    ctx.closePath()
    return
  }
  ctx.arc(x, y, r, 0, 6.2832)
}

function prefersReducedMotion() {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function NetworkCanvas({
  net,
  onOpen,
  focus,
  anchor = false,
}: {
  net: Network
  onOpen: (id: string) => void
  /** the record the peek panel is showing: haloed, and its neighbourhood lifted */
  focus?: string | undefined
  /**
   * Hold the focused record at the middle. A detail page's graph is ABOUT one record, so
   * the layout shouldn't decide where it sits: without this the camera frames the
   * bounding box and the densest cluster takes the centre, which reads as "this graph is
   * about that other thing".
   */
  anchor?: boolean
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const tipRef = useRef<HTMLDivElement>(null)
  // The animation loop reads the latest graph through a ref, so new events restyle the
  // running simulation instead of restarting it and throwing the layout away.
  const dataRef = useRef({ net, onOpen, focus, anchor })
  dataRef.current = { net, onOpen, focus, anchor }

  useEffect(() => {
    const canvas = canvasRef.current
    const tipEl = tipRef.current
    if (!canvas || !tipEl) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const tip: HTMLDivElement = tipEl

    const bodies = new Map<string, Body>()
    const cam = { x: 0, y: 0, scale: 0.9 }
    let DPR = 1
    let CW = 0
    let CH = 0
    let hover: string | null = null
    let drag: string | null = null
    let moved = false
    let pan = false
    let lx = 0
    let ly = 0
    let settle = 1
    // Auto-fit stays on until the first deliberate pan or zoom: after that the view is
    // the user's, and moving it under them would be rude.
    let autoFit = true
    // The anchor holds until you drag the anchored node yourself.
    let pinned = true

    function resize() {
      DPR = Math.min(devicePixelRatio || 1, 2)
      const r = canvas!.getBoundingClientRect()
      CW = r.width
      CH = r.height
      canvas!.width = Math.max(1, CW * DPR)
      canvas!.height = Math.max(1, CH * DPR)
      ctx!.setTransform(DPR, 0, 0, DPR, 0, 0)
    }
    resize()
    const ro = new ResizeObserver(resize)
    ro.observe(canvas)

    const scr = (wx: number, wy: number): [number, number] => [
      (wx - cam.x) * cam.scale + CW / 2,
      (wy - cam.y) * cam.scale + CH / 2,
    ]
    const wld = (sx: number, sy: number): [number, number] => [
      (sx - CW / 2) / cam.scale + cam.x,
      (sy - CH / 2) / cam.scale + cam.y,
    ]

    const radius = (n: NetNode) => Math.min(30, 7 + Math.sqrt(n.degree) * 3.4)

    /** Sync bodies with the graph, seeding newcomers on a ring so nothing starts stacked. */
    function sync(nodes: NetNode[]) {
      const ids = new Set(nodes.map((n) => n.id))
      for (const id of [...bodies.keys()]) if (!ids.has(id)) bodies.delete(id)
      nodes.forEach((n, i) => {
        const b = bodies.get(n.id)
        if (b) {
          b.r += (radius(n) - b.r) * 0.15
          return
        }
        const a = (i / Math.max(1, nodes.length)) * Math.PI * 2
        const spread = 120 + Math.min(260, nodes.length * 6)
        bodies.set(n.id, {
          x: Math.cos(a) * spread,
          y: Math.sin(a) * spread,
          vx: 0,
          vy: 0,
          r: radius(n),
        })
      })
    }

    function nodeAt(sx: number, sy: number): string | null {
      const { nodes } = dataRef.current.net
      for (let i = nodes.length - 1; i >= 0; i--) {
        const n = nodes[i]!
        const b = bodies.get(n.id)
        if (!b) continue
        const [nx, ny] = scr(b.x, b.y)
        const r = b.r * cam.scale
        if ((sx - nx) ** 2 + (sy - ny) ** 2 <= (r + 5) ** 2) return n.id
      }
      return null
    }

    function onMove(e: MouseEvent) {
      const rect = canvas!.getBoundingClientRect()
      const sx = e.clientX - rect.left
      const sy = e.clientY - rect.top
      if (drag) {
        // Dragging the anchored node releases the anchor: it would otherwise snap back
        // under the cursor, which reads as the canvas fighting you.
        if (drag === dataRef.current.focus) pinned = false
        const b = bodies.get(drag)
        if (b) {
          const [wx, wy] = wld(sx, sy)
          b.x = wx
          b.y = wy
          b.vx = 0
          b.vy = 0
          moved = true
          settle = 1
        }
        return
      }
      if (pan) {
        autoFit = false
        cam.x -= (e.clientX - lx) / cam.scale
        cam.y -= (e.clientY - ly) / cam.scale
        lx = e.clientX
        ly = e.clientY
        return
      }
      hover = nodeAt(sx, sy)
      canvas!.style.cursor = hover ? 'pointer' : 'grab'
      const node = hover ? dataRef.current.net.nodes.find((n) => n.id === hover) : null
      if (node) {
        tip.textContent = `${KINDS[node.kind].singular} · ${node.label} · ${node.degree} ${node.degree === 1 ? 'link' : 'links'}`
        tip.style.left = `${Math.min(e.clientX + 14, innerWidth - 260)}px`
        tip.style.top = `${e.clientY + 16}px`
        tip.style.opacity = '1'
      } else {
        tip.style.opacity = '0'
      }
    }

    function onDown(e: MouseEvent) {
      const rect = canvas!.getBoundingClientRect()
      lx = e.clientX
      ly = e.clientY
      const id = nodeAt(e.clientX - rect.left, e.clientY - rect.top)
      if (id) {
        drag = id
        moved = false
      } else {
        pan = true
        canvas!.style.cursor = 'grabbing'
      }
    }

    function onUp() {
      // A press that didn't move is a click: open the record.
      if (drag && !moved) dataRef.current.onOpen(drag)
      drag = null
      pan = false
      canvas!.style.cursor = hover ? 'pointer' : 'grab'
    }

    function onWheel(e: WheelEvent) {
      e.preventDefault()
      autoFit = false
      const rect = canvas!.getBoundingClientRect()
      const mx = e.clientX - rect.left
      const my = e.clientY - rect.top
      const [wx, wy] = wld(mx, my)
      cam.scale = Math.max(0.25, Math.min(2.6, cam.scale * Math.exp(-e.deltaY * 0.0014)))
      cam.x = wx - (mx - CW / 2) / cam.scale
      cam.y = wy - (my - CH / 2) / cam.scale
    }

    function onLeave() {
      tip.style.opacity = '0'
      hover = null
    }

    canvas.addEventListener('mousemove', onMove)
    canvas.addEventListener('mousedown', onDown)
    window.addEventListener('mouseup', onUp)
    canvas.addEventListener('wheel', onWheel, { passive: false })
    canvas.addEventListener('mouseleave', onLeave)

    const reduced = prefersReducedMotion()
    let raf = 0
    let lastCount = -1

    function frame() {
      const { net: g, focus: focused } = dataRef.current
      sync(g.nodes)
      // A changed node count means the shape changed: let it re-settle.
      if (g.nodes.length !== lastCount) {
        lastCount = g.nodes.length
        settle = 1
      }

      const css = getComputedStyle(document.documentElement)
      const ink = css.getPropertyValue('--fg').trim() || '#241f19'
      const money = css.getPropertyValue('--ember').trim() || '#a8501f'
      const work = css.getPropertyValue('--marine').trim() || '#1d5f6a'
      const faint = css.getPropertyValue('--faint').trim() || '#9a8f7f'
      const hueOf = (k: EdgeKind) =>
        EDGE_HUE[k] === 'money' ? money : EDGE_HUE[k] === 'work' ? work : faint
      // Each kind's own colour, read from the theme so light and dark both work.
      const kindHue = (k: EntityKind) =>
        css.getPropertyValue(accentVar(k)).trim() || (KINDS[k].hue === 'work' ? work : money)

      // ── physics ───────────────────────────────────────────────────────────
      // Repulsion is O(n²), which is fine to a few hundred nodes and keeps the
      // implementation honest; the alpha term stops it churning once it has settled.
      if (settle > 0.002) {
        const alpha = reduced ? 1 : settle
        // Repulsion has to grow with the crowd: a constant that looks right for a dozen
        // nodes packs fifty into a ball. Scaling by count keeps the density even.
        const REP = 2600 + g.nodes.length * 260
        for (let i = 0; i < g.nodes.length; i++) {
          const a = bodies.get(g.nodes[i]!.id)!
          for (let j = i + 1; j < g.nodes.length; j++) {
            const b = bodies.get(g.nodes[j]!.id)!
            let dx = a.x - b.x
            let dy = a.y - b.y
            let d2 = dx * dx + dy * dy
            if (d2 < 1) {
              // Two nodes exactly on top of each other have no direction to separate
              // along, so give them one rather than dividing by zero.
              dx = (i % 2 ? 1 : -1) * 0.5
              dy = (j % 2 ? 1 : -1) * 0.5
              d2 = 1
            }
            const d = Math.sqrt(d2)
            const f = (REP / d2) * alpha
            const fx = (dx / d) * f
            const fy = (dy / d) * f
            a.vx += fx
            a.vy += fy
            b.vx -= fx
            b.vy -= fy
          }
        }
        for (const e of g.edges) {
          const a = bodies.get(e.from)
          const b = bodies.get(e.to)
          if (!a || !b) continue
          const dx = b.x - a.x
          const dy = b.y - a.y
          const d = Math.hypot(dx, dy) || 1
          const f = (d - (REST[e.kind] + a.r + b.r)) * STIFF[e.kind] * alpha
          const fx = (dx / d) * f
          const fy = (dy / d) * f
          a.vx += fx
          a.vy += fy
          b.vx -= fx
          b.vy -= fy
        }
        const anchored = dataRef.current.anchor && pinned ? focused : null
        for (const n of g.nodes) {
          const b = bodies.get(n.id)!
          if (n.id === drag) {
            b.vx = 0
            b.vy = 0
            continue
          }
          if (n.id === anchored) {
            // Held at the world origin. Its springs still pull everything else INTO a
            // ring around it, which is exactly the shape a detail page wants.
            b.x = 0
            b.y = 0
            b.vx = 0
            b.vy = 0
            continue
          }
          // Weak centring only: enough to stop islands drifting off, not enough to
          // squeeze the graph into the middle of the canvas.
          b.vx += -b.x * 0.0012
          b.vy += -b.y * 0.0012
          b.vx *= 0.84
          b.vy *= 0.84
          b.x += b.vx
          b.y += b.vy
        }
        settle *= 0.985
      }

      // Frame the graph, eased so it doesn't snap around while the layout still moves.
      if (autoFit && g.nodes.length > 0) {
        // Proportional padding: a fixed 68px is a third of the 300px box the detail
        // pages give this, which left the graph pinned tiny in the middle.
        const pad = Math.min(68, Math.min(CW, CH) * 0.11)
        const anchorBody = dataRef.current.anchor && focused ? bodies.get(focused) : undefined

        if (anchorBody) {
          // Centre ON the anchored record and scale to the furthest thing from it, per
          // axis, so it stays put while its surroundings settle around it.
          let dx = 60
          let dy = 60
          for (const n of g.nodes) {
            const b = bodies.get(n.id)!
            dx = Math.max(dx, Math.abs(b.x - anchorBody.x) + b.r)
            dy = Math.max(dy, Math.abs(b.y - anchorBody.y) + b.r)
          }
          const want = Math.min(
            1.6,
            Math.max(0.28, Math.min((CW / 2 - pad) / dx, (CH / 2 - pad) / dy)),
          )
          cam.scale += (want - cam.scale) * 0.06
          cam.x += (anchorBody.x - cam.x) * 0.12
          cam.y += (anchorBody.y - cam.y) * 0.12
        } else {
          let minX = Infinity
          let minY = Infinity
          let maxX = -Infinity
          let maxY = -Infinity
          for (const n of g.nodes) {
            const b = bodies.get(n.id)!
            minX = Math.min(minX, b.x - b.r)
            minY = Math.min(minY, b.y - b.r)
            maxX = Math.max(maxX, b.x + b.r)
            maxY = Math.max(maxY, b.y + b.r)
          }
          const want = Math.min(
            1.6,
            Math.max(
              0.28,
              Math.min(
                (CW - pad * 2) / Math.max(60, maxX - minX),
                (CH - pad * 2) / Math.max(60, maxY - minY),
              ),
            ),
          )
          cam.scale += (want - cam.scale) * 0.06
          cam.x += ((minX + maxX) / 2 - cam.x) * 0.08
          cam.y += ((minY + maxY) / 2 - cam.y) * 0.08
        }
      }

      // ── draw ──────────────────────────────────────────────────────────────
      ctx!.clearRect(0, 0, CW, CH)

      const neighbours = new Set<string>()
      const lit = hover ?? focused ?? null
      const dimming = hover !== null
      if (lit) {
        neighbours.add(lit)
        for (const e of g.edges) {
          if (e.from === lit) neighbours.add(e.to)
          if (e.to === lit) neighbours.add(e.from)
        }
      }

      ctx!.lineCap = 'round'
      for (const e of g.edges) {
        const a = bodies.get(e.from)
        const b = bodies.get(e.to)
        if (!a || !b) continue
        const [ax, ay] = scr(a.x, a.y)
        const [bx, by] = scr(b.x, b.y)
        const near = !lit || (neighbours.has(e.from) && neighbours.has(e.to))
        const on = near || !dimming
        ctx!.strokeStyle = withAlpha(hueOf(e.kind), on ? (e.kind === 'mention' ? 0.3 : 0.5) : 0.07)
        ctx!.lineWidth = near && lit ? 1.8 : e.kind === 'mention' ? 0.9 : 1.3
        // Prose edges are dashed: they're a person's sentence, not a structural fact.
        ctx!.setLineDash(e.kind === 'mention' ? [3, 4] : [])
        // A slight bow keeps two edges between the same pair from overprinting.
        const mx = (ax + bx) / 2
        const my = (ay + by) / 2
        const nx = -(by - ay)
        const ny = bx - ax
        const nl = Math.hypot(nx, ny) || 1
        const bow = 9 * cam.scale
        ctx!.beginPath()
        ctx!.moveTo(ax, ay)
        ctx!.quadraticCurveTo(mx + (nx / nl) * bow, my + (ny / nl) * bow, bx, by)
        ctx!.stroke()
      }
      ctx!.setLineDash([])

      const drawn: [number, number, number, number][] = []
      const free = (x: number, y: number, w: number, h: number) =>
        !drawn.some((d) => x < d[2] && d[0] < x + w && y < d[3] && d[1] < y + h)

      // Biggest first, so when two labels compete the more connected record keeps its
      // name — which is the one you were more likely looking for.
      for (const n of [...g.nodes].sort((a, z) => z.degree - a.degree)) {
        const b = bodies.get(n.id)!
        const [sx, sy] = scr(b.x, b.y)
        const r = b.r * cam.scale
        const hue = kindHue(n.kind)
        const on = !lit || neighbours.has(n.id) || !dimming
        const a = on ? 1 : 0.16
        const isLit = n.id === lit

        if (isLit) {
          const glow = ctx!.createRadialGradient(sx, sy, r * 0.5, sx, sy, r * 2.6)
          glow.addColorStop(0, withAlpha(hue, 0.28))
          glow.addColorStop(1, withAlpha(hue, 0))
          ctx!.fillStyle = glow
          ctx!.beginPath()
          ctx!.arc(sx, sy, r * 2.6, 0, 6.2832)
          ctx!.fill()
        }

        nodePath(ctx!, n.kind, sx, sy, r)
        ctx!.fillStyle = withAlpha(hue, 0.22 * a)
        ctx!.fill()
        ctx!.lineWidth = isLit ? 2 : 1.2
        ctx!.strokeStyle = withAlpha(hue, 0.9 * a)
        ctx!.stroke()

        // A person is a ring with a dot: it keeps the circle (people aren't a
        // container or an instrument) while still reading as its own thing.
        if (n.kind === 'person') {
          ctx!.beginPath()
          ctx!.arc(sx, sy, Math.max(1.8, r * 0.34), 0, 6.2832)
          ctx!.fillStyle = withAlpha(hue, 0.95 * a)
          ctx!.fill()
        }

        const wants = r > 13 || isLit || (lit ? neighbours.has(n.id) : false)
        if (wants && a > 0.3) {
          ctx!.font = `${n.kind === 'org' ? '600 ' : ''}11.5px ui-sans-serif, system-ui, sans-serif`
          ctx!.textAlign = 'center'
          ctx!.textBaseline = 'top'
          let text = n.label
          const max = 140
          if (ctx!.measureText(text).width > max) {
            while (text.length > 3 && ctx!.measureText(`${text}…`).width > max)
              text = text.slice(0, -1)
            text += '…'
          }
          const w = ctx!.measureText(text).width
          const x = sx - w / 2
          const y = sy + r + 4
          // Anything lit gets its label regardless: you asked for that one.
          if (isLit || free(x - 2, y - 1, w + 4, 15)) {
            drawn.push([x - 2, y - 1, x + w + 2, y + 14])
            ctx!.fillStyle = withAlpha(ink, 0.86 * a)
            ctx!.fillText(text, sx, y)
          }
        }
      }

      raf = requestAnimationFrame(frame)
    }
    raf = requestAnimationFrame(frame)

    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
      canvas.removeEventListener('mousemove', onMove)
      canvas.removeEventListener('mousedown', onDown)
      window.removeEventListener('mouseup', onUp)
      canvas.removeEventListener('wheel', onWheel)
      canvas.removeEventListener('mouseleave', onLeave)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="absolute inset-0">
      <canvas ref={canvasRef} className="block h-full w-full" style={{ cursor: 'grab' }} />
      <div
        ref={tipRef}
        className="rounded-control text-meta pointer-events-none fixed z-50 max-w-[260px] border px-2.5 py-1.5 shadow-lg"
        style={{
          opacity: 0,
          transition: 'opacity .12s',
          background: 'var(--panel)',
          borderColor: 'var(--border)',
        }}
      />
    </div>
  )
}

/** A CSS hex from the theme plus an alpha, as a canvas-ready colour. */
function withAlpha(hex: string, alpha: number): string {
  const s = hex.replace('#', '')
  const full =
    s.length === 3
      ? s
          .split('')
          .map((c) => c + c)
          .join('')
      : s
  const r = parseInt(full.slice(0, 2), 16)
  const g = parseInt(full.slice(2, 4), 16)
  const b = parseInt(full.slice(4, 6), 16)
  if ([r, g, b].some(Number.isNaN)) return `rgba(140,140,140,${alpha})`
  return `rgba(${r},${g},${b},${Math.max(0, Math.min(1, alpha))})`
}

/** Used by NetEdge consumers that want a stable key. */
export function edgeKey(e: NetEdge): string {
  return `${e.from}~${e.to}~${e.kind}`
}
